/**
 * Calendar subscription utilities — platform detection + deep-link URL builders.
 *
 * Issue #1513: Google Calendar feed subscription — solve Android webcal:// gap
 *
 * NOTE: The Google Calendar deep link (`/calendar/r?cid=webcal://...`) is
 * undocumented behavior. It works in practice but is not a Google API contract.
 * Always provide manual fallback (copy URL + instructions).
 */

export type CalendarProvider = 'google' | 'apple' | 'outlook';

/**
 * Detect which calendar platform to visually emphasize.
 * Returns null when no strong signal — all buttons shown equally.
 * This controls visual emphasis only, never hides options.
 */
export function detectCalendarPlatform(): CalendarProvider | null {
  const ua = navigator.userAgent;

  // iOS devices (iPhone, iPad, iPod)
  if (/iphone|ipad|ipod/i.test(ua)) {
    return 'apple';
  }

  // iPadOS Safari reports "Macintosh" UA — detect via touch capability
  if (/macintosh/i.test(ua) && navigator.maxTouchPoints > 1) {
    return 'apple';
  }

  // Android
  if (/android/i.test(ua)) {
    return 'google';
  }

  return null;
}

/**
 * Convert an HTTPS feed URL to webcal:// protocol.
 * String-based to stay portable across WHATWG URL implementations: jsdom
 * (and other strict parsers) silently no-op when assigning a non-special
 * scheme like webcal: to a URL constructed from https. Browsers accept it
 * in practice; string-replace works the same everywhere.
 */
function toWebcalUrl(feedUrl: string): string {
  return feedUrl.replace(/^https?:/, 'webcal:');
}

/**
 * Google Calendar subscription deep link.
 * Uses undocumented `cid` parameter with webcal:// protocol.
 */
export function buildGoogleCalendarUrl(feedUrl: string): string {
  const webcalUrl = toWebcalUrl(feedUrl);
  return `https://calendar.google.com/calendar/r?cid=${encodeURIComponent(webcalUrl)}`;
}

/**
 * Apple Calendar subscription URL (webcal:// triggers native dialog on iOS/macOS).
 */
export function buildAppleCalendarUrl(feedUrl: string): string {
  return toWebcalUrl(feedUrl);
}

/**
 * Outlook web subscription URL.
 */
export function buildOutlookSubscribeUrl(feedUrl: string, calendarName: string): string {
  const params = new URLSearchParams({
    url: feedUrl,
    name: calendarName,
  });
  return `https://outlook.live.com/calendar/0/addfromweb?${params.toString()}`;
}
